import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styles from "./NavBar.module.css";
import Image from "next/image";
import MobileSidebar from "./MobileSidebar";
import FlexibleMegamenu from "./FlexibleMegamenu";
import { handleLinkClick } from "@/Config/helper";
import CustomiseCta from "../CustomiseCta/CustomiseCta";
import { fetchMegamenuData } from "@/services/CmsService";
import { fetchCommonData } from "@/services/CommonService";
import { getLocaleFromPath, removeCountryPrefix } from "@/Config/countryConstants";

interface NavBarTwoProps {
  handleEnrollButtonClick: () => void;
  handleBroutcherButtonClick: (broutcherLink?: string) => void;
  pageTitle?: any;
  data?: any;
  popUpData?: any;
}

const NavTwo: React.FC<NavBarTwoProps> = ({
  handleBroutcherButtonClick,
  pageTitle,
  data,
  popUpData,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [megamenuData, setMegamenuData] = useState<any>(null);
  const [fallbackNavData, setFallbackNavData] = useState<any>(null);
  const router = useRouter();

  // Get current country from path
  const getCurrentCountry = () => {
    const pathSegments = router.asPath.split('/').filter(Boolean);
    const knownCountries = ['india', 'kenya', 'nigeria', 'south-africa'];
    return knownCountries.includes(pathSegments[0]) ? pathSegments[0] : null;
  };

  const currentCountry = getCurrentCountry();

  // Case-insensitive check for labels like "Country", "country", "COUNTRY-India", etc. (following corporate education pattern)
  const isCountryMenuLabel = (label?: string) => {
    if (!label) return false;
    const normalized = label.trim().toLowerCase();
    return normalized === 'country' || normalized.startsWith('country');
  };

  // Selected country label suffix for Country menu (following corporate education pattern)
  const getSelectedCountryLabelSuffix = () => {
    const CODE_MAP: Record<string, string> = {
      'india': 'IN',
      'kenya': 'KE',
      'nigeria': 'NG',
      'south-africa': '',
    };
    const code = CODE_MAP[currentCountry || 'south-africa'];
    if (!code || currentCountry === 'south-africa') return '';
    return ` (${code})`;
  };

  // Simple function to manipulate country links - convert external URLs to internal routes
  const manipulateCountryLinks = (navData: any) => {
    if (!navData || !navData.main_nav_links) return navData;

    const currentPath = router.asPath;
    const cleanPath = currentPath?.split('?')[0]?.split('#')[0];
    
    // Remove country prefix to get the base path
    const pathWithoutCountry = removeCountryPrefix(cleanPath);
    
    // Preserve path for ANY non-root page (e.g., "/master-of-business-administration" -> "/india/master-of-business-administration").
    // Root ("/") goes to country root (e.g., "/india").
    const isPreservePathPage = pathWithoutCountry !== "/";

    // Clone the navigation data to avoid mutating the original
    const manipulatedData = JSON.parse(JSON.stringify(navData));

    // Process main navigation links
    manipulatedData.main_nav_links = manipulatedData?.main_nav_links?.map((item: any) => {
      // Handle Country dropdown - check if this is the Country item
      if (isCountryMenuLabel(item?.label) && item?.nested_list_links) {
        // Add Global option at the beginning
        const globalOption = {
          id: 9999,
          title: "South Africa",
          url: pathWithoutCountry, // Always use the path without country prefix for South Africa (global)
          links: [],
          lists: []
        };
        
        const manipulatedCountryLinks = [
          globalOption,
          ...item.nested_list_links
            .filter((countryItem: any) => 
              // Filter out any existing South Africa entries to avoid duplicates
              countryItem?.title?.toLowerCase() !== "south africa" && 
              countryItem?.title?.toLowerCase() !== "global"
            )
            .map((countryItem: any) => {
            // Store original URL for debugging
            const originalUrl = countryItem?.url;
            console.log("originalUrl", originalUrl);
            // Extract country code from URL (e.g., "nigeria" from "https://www.nigeria.regenesys.net/")
            let countryCode = "";
            
            // Check if it's a relative path (starts with /)
            if (countryItem.url.startsWith('/')) {
              countryCode = countryItem.url.replace('/', '');
            } else {
              // It's an external URL, try to parse it
              try {
                const url = new URL(countryItem.url);
                const hostname = url.hostname;
                // Extract country from subdomain (e.g., "nigeria" from "nigeria.regenesys.net")
                const subdomain = hostname.split('.')[0];
                countryCode = subdomain;
              } catch (error) {
                // Fallback: try to extract from URL string
                console.log("error", error);
                const match = countryItem.url.match(/https?:\/\/www\.([^.]+)\./);
                countryCode = match ? match[1] : "";
              }
            }
            
            if (isPreservePathPage) {
              // For events, programs, schools pages, preserve the full path
              countryItem.url = `/${countryCode}${pathWithoutCountry}`;
            } else {
              // For other pages, use country root
              countryItem.url = `/${countryCode}`;
            }
            
            return countryItem;
          })
        ];
        
        item.nested_list_links = manipulatedCountryLinks;
      }
      return item;
    });

    return manipulatedData;
  };

  const feesBrochure = data?.fees_brouchure?.pdf_link?.url || "";
  const prospectusBrochure = data?.prospectous_brouchure?.pdf_link?.url || "";
  const isAlumniPage = router.pathname === "/alumni";

  useEffect(() => {
    const fetchMegamenu = async () => {
      try {
        // Detect locale from current path for country-aware megamenu
        const cleanPath = (router.asPath || "/").split('?')[0].split('#')[0];
        const locale = getLocaleFromPath(cleanPath);
        const data = await fetchMegamenuData(locale);
        const apiData = data.data || data;

        const hasExpectedStructure =
          apiData?.mega_menu_header?.main_nav_links &&
          Array.isArray(apiData.mega_menu_header.main_nav_links);

        if (hasExpectedStructure) {
          const transformedData = {
            categories: apiData.mega_menu_header.main_nav_links || [],
            certificationCourses: apiData.mega_menu_header
              ?.certification_courses || {
              title: "",
              url: "",
              items: [],
            },
          };
          setMegamenuData(transformedData);
        } else {
          const fallbackData = {
            categories: apiData?.mega_menu_header?.main_nav_links || [],
            certificationCourses: apiData?.mega_menu_header
              ?.certification_courses || {
              title: "",
              url: "",
              items: [],
            },
          };
          if (
            fallbackData.categories.length > 0 ||
            (fallbackData.certificationCourses.title &&
              fallbackData.certificationCourses.title.trim() !== "")
          ) {
            setMegamenuData(fallbackData);
          } else {
            setMegamenuData(null);
          }
        }
      } catch (error: any) {
        console.error("Error fetching megamenu data:", error);
        setMegamenuData({
          categories: [],
          certificationCourses: {
            title: "",
            url: "",
            items: [],
          },
        });
      }
    };

    fetchMegamenu();
  }, []);

  useEffect(() => {
    const fetchFallbackNavData = async () => {
      if (!data?.main_nav_links) {
        try {
          // Determine locale based on current country
          const currentPath = router.asPath;
          const cleanPath = currentPath.split('?')[0].split('#')[0];
          
          // Use country-specific locale or default to 'en'
          const locale = getLocaleFromPath(cleanPath);
          
          const commonData = await fetchCommonData(locale);
          setFallbackNavData(commonData?.data?.header);
        } catch (error: any) {
          console.error("Error fetching fallback nav data:", error);
        }
      }
    };

    fetchFallbackNavData();
  }, [data?.main_nav_links, router.asPath]);


  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    if (typeof window !== "undefined") {
      checkIfMobile();
      window.addEventListener("resize", checkIfMobile);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", checkIfMobile);
      }
    };
  }, []);

  const logoAlumniWidth = isAlumniPage
    ? {
        url: "/assets/alumni-logo.svg",
        alternativeText: "Regenesys Alumni Logo",
        width: isMobile ? 142 : 204,
        height: isMobile ? 34 : 49,
      }
    : {
        url: data?.logo?.url,
        alternativeText: data?.logo?.alternativeText || "Regenesys Logo",
        width: isMobile ? 142 : 204,
        height: isMobile ? 28 : 40,
      };
  const logo =
    router.pathname === "/alumni"
      ? {
          url: "/assets/Regenesys_Alumni_Logo_White.png",
          alternativeText: "Regenesys Alumni Logo",
        }
      : {
          url: data?.logo?.url,
          alternativeText: data?.logo?.alternativeText || "Regenesys Logo",
        };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);

      const handleRouteChange = () => {
        setDropdownOpen(null);
      };

      router.events.on("routeChangeStart", handleRouteChange);

      return () => {
        window.removeEventListener("scroll", handleScroll);
        router.events.off("routeChangeStart", handleRouteChange);
      };
    }
  }, [router.events]);

  const handleMouseEnter = (index: number) => {
    setDropdownOpen(index);
  };

  const handleMouseLeave = () => {
    setDropdownOpen(null);
  };

  const handleCloseDropdown = () => {
    setDropdownOpen(null);
  };

  return (
    <>
      <div className={styles.mainNavContainer}>
        <div
          className={`${styles.navTwoContainer} ${
            scrolled ? styles.scrolled : ""
          }`}
          style={{
            backgroundColor: pageTitle === "404 Error" ? "#18463e" : "none",
          }}
        >
          <div className={styles.navContent}>
            <div className={styles.logoContainer}>
              <Link href="/" title={logo.alternativeText}>
                <Image
                  title={logo.alternativeText}
                  src={logo.url}
                  alt={logo.alternativeText}
                  width={204}
                  height={40}
                  className={styles.LogoImg}
                  style={{
                    width: logoAlumniWidth.width,
                    height: logoAlumniWidth.height,
                  }}
                />
              </Link>
            </div>

            <div className={styles.mobileApplyButton}>
              <CustomiseCta
                btnLabelDesktop="Study Enquiry"
                position="Top"
                ctaData={data?.study_enquiry_cta}
                preEnrollmentEnable
                mixpanelEvent="study_enquiry_clicked"
                popupData={popUpData}
              />
            </div>

            <nav className={styles.navTwo}>
              {manipulateCountryLinks(data || fallbackNavData)?.main_nav_links?.map(
                (item: any, index: number) => (
                  <div
                    key={index}
                    className={`${styles.navItem} ${
                      item?.nested_list_links?.length > 0 ||
                      (item?.label === "Study at Regenesys" && megamenuData)
                        ? styles.hasSubmenu
                        : ""
                    }`}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div onClick={() => setDropdownOpen(null)}>
                      <Link
                        href={item?.url}
                        className={
                          item?.label === "Apply"
                            ? styles.applyButton
                            : styles.navLinkTwo
                        }
                        aria-label={isCountryMenuLabel(item?.label)
                          ? `Country${getSelectedCountryLabelSuffix()}`
                          : item?.label}
                        title={isCountryMenuLabel(item?.label)
                          ? `Country${getSelectedCountryLabelSuffix()}`
                          : item?.label}
                      >
                        {isCountryMenuLabel(item?.label)
                          ? `Country${getSelectedCountryLabelSuffix()}`
                          : item?.label}
                        {(item?.nested_list_links?.length > 0 ||
                          (item?.label === "Study at Regenesys" &&
                            megamenuData)) && (
                          <Image
                            src="/assets/DropDownArrow.svg"
                            alt="DropDown Arrow"
                            title="DropDown Arrow"
                            width={12}
                            height={7}
                            className={`${styles.dropDownImg} ${
                              dropdownOpen === index ? styles.rotateArrow : ""
                            }`}
                          />
                        )}
                      </Link>
                    </div>

                    {(item?.nested_list_links?.length > 0 ||
                      (item?.label === "Study at Regenesys" &&
                        megamenuData)) && (
                      <div
                        className={
                          item?.label === "Study at Regenesys"
                            ? styles.submenu
                            : styles.submenuDropDown
                        }
                        onMouseEnter={() => handleMouseEnter(index)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <div className={styles.submenuItems}>
                          {item?.label === "Study at Regenesys" ? (
                            <FlexibleMegamenu
                              onClose={handleCloseDropdown}
                              megamenuData={megamenuData}
                            />
                          ) : (
                            <div
                              className={styles.megamenuFlexDropDown}
                              style={{
                                display: "block",
                              }}
                            >
                              {item?.nested_list_links?.map(
                                (subItem: any, subIndex: number) => {
                                  const isEducation =
                                    subItem?.title?.toLowerCase() ===
                                    "school of education";
                                  const isPublicManagement =
                                    subItem?.title?.toLowerCase() ===
                                    "school of public management";

                                  if (isEducation) return null;

                                  return (
                                    <div
                                      key={subIndex}
                                      className={styles.submenuSection}
                                    >
                                      {subItem?.title === "Fees" ||
                                      subItem?.title === "Prospectus" ? (
                                        <button
                                          onClick={() => {
                                            handleBroutcherButtonClick(
                                              subItem?.title === "Fees"
                                                ? feesBrochure
                                                : prospectusBrochure
                                            );
                                            setDropdownOpen(null);
                                          }}
                                          className={styles.submenuTitleButton}
                                        >
                                          {subItem?.title}
                                        </button>
                                      ) : (
                                        <div
                                          onClick={() => setDropdownOpen(null)}
                                        >
                                          <Link
                                            href={subItem?.url}
                                            title={subItem?.title}
                                            className={styles.submenuTitle}
                                            onClick={(e) =>
                                              handleLinkClick(
                                                e,
                                                subItem?.url,
                                                router
                                              )
                                            }
                                          >
                                            <h3>{subItem?.title}</h3>
                                          </Link>
                                        </div>
                                      )}

                                      {subItem?.lists?.map(
                                        (
                                          section: any,
                                          sectionIndex: number
                                        ) => (
                                          <div
                                            key={sectionIndex}
                                            className={styles.sectionMenuItems}
                                          >
                                            <Link
                                              href={section?.url}
                                              className={styles.sectionTitle}
                                              title={section?.title}
                                              onClick={() =>
                                                setDropdownOpen(null)
                                              }
                                            >
                                              <h4>{section?.title}</h4>
                                            </Link>
                                            {section?.items?.map(
                                              (
                                                item: any,
                                                itemIndex: number
                                              ) => (
                                                <Link
                                                  key={itemIndex}
                                                  href={item?.url}
                                                  className={styles.submenuItem}
                                                  title={item?.label}
                                                  onClick={() =>
                                                    setDropdownOpen(null)
                                                  }
                                                >
                                                  {item?.label}
                                                </Link>
                                              )
                                            )}
                                          </div>
                                        )
                                      )}

                                      {subItem?.links?.map(
                                        (linkItem: any, linkIndex: number) => (
                                          <Link
                                            key={linkIndex}
                                            href={linkItem?.url}
                                            title={linkItem?.label}
                                            className={styles.submenuItem}
                                            onClick={() =>
                                              setDropdownOpen(null)
                                            }
                                          >
                                            {linkItem?.label}
                                          </Link>
                                        )
                                      )}

                                      {isPublicManagement &&
                                        item?.nested_list_links
                                          ?.filter(
                                            (n: any) =>
                                              n?.title?.toLowerCase() ===
                                              "school of education"
                                          )
                                          ?.map((eduBlock: any) => (
                                            <div
                                              key={eduBlock?.id}
                                              style={{ marginTop: 10 }}
                                            >
                                              <Link
                                                href={eduBlock?.url}
                                                title={eduBlock?.title}
                                                className={styles.submenuTitle}
                                                onClick={(e) =>
                                                  handleLinkClick(
                                                    e,
                                                    eduBlock?.url,
                                                    router
                                                  )
                                                }
                                              >
                                                <h3>{eduBlock?.title}</h3>
                                              </Link>

                                              {eduBlock?.lists?.map(
                                                (
                                                  section: any,
                                                  sectionIndex: number
                                                ) => (
                                                  <div
                                                    key={sectionIndex}
                                                    className={
                                                      styles.sectionMenuItems
                                                    }
                                                  >
                                                    <Link
                                                      href={section?.url}
                                                      className={
                                                        styles.sectionTitle
                                                      }
                                                      title={section?.title}
                                                      onClick={() =>
                                                        setDropdownOpen(null)
                                                      }
                                                    >
                                                      <h4>{section?.title}</h4>
                                                    </Link>
                                                    {section?.items?.map(
                                                      (
                                                        item: any,
                                                        itemIndex: number
                                                      ) => (
                                                        <Link
                                                          key={itemIndex}
                                                          href={item?.url}
                                                          title={item?.label}
                                                          className={
                                                            styles.submenuItem
                                                          }
                                                          onClick={() =>
                                                            setDropdownOpen(
                                                              null
                                                            )
                                                          }
                                                        >
                                                          {item?.label}
                                                        </Link>
                                                      )
                                                    )}
                                                  </div>
                                                )
                                              )}

                                              {eduBlock?.links?.map(
                                                (link: any, i: number) => (
                                                  <Link
                                                    key={i}
                                                    href={link?.url}
                                                    title={link?.label}
                                                    className={
                                                      styles.submenuItem
                                                    }
                                                    onClick={() =>
                                                      setDropdownOpen(null)
                                                    }
                                                  >
                                                    {link?.label}
                                                  </Link>
                                                )
                                              )}
                                            </div>
                                          ))}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}

              <div className={styles.navItem}>
                <div
                  className={styles.applyButton}
                  data-interaction="Nav CTA Click"
                >
                  <CustomiseCta
                    btnLabelDesktop="Study Enquiry"
                    position="Top"
                    ctaData={data?.study_enquiry_cta}
                    popupData={popUpData}
                    preEnrollmentEnable
                    mixpanelEvent="study_enquiry_clicked"
                  />
                </div>
              </div>
            </nav>
          </div>
        </div>
        <MobileSidebar
          apiData={manipulateCountryLinks(data || fallbackNavData)?.main_nav_links}
          data={data || fallbackNavData}
          handleBroutcherButtonClick={handleBroutcherButtonClick}
          feesBrochure={feesBrochure}
          prospectusBrochure={prospectusBrochure}
          megamenuData={megamenuData}
        />
      </div>
    </>
  );
};

export default NavTwo;
